"use client";

import * as React from "react";
import * as RPopover from "@radix-ui/react-popover";
import { cn } from "./cn";

/**
 * Radix-based Popover. Radix positions via Floating UI, which measures against
 * the VisualViewport API and runs collision detection — so the panel flips and
 * shifts to stay inside the *visible* area. This is what a hand-rolled
 * `position: fixed` + `getBoundingClientRect()` popover gets wrong on iOS
 * Safari, where the dynamic toolbar makes the visual viewport differ from the
 * layout viewport that `window.innerHeight` reports (the panel ends up below
 * the modal / behind the toolbar and can't be tapped).
 *
 * Mirrors the positioning approach already used by `Select` in this package.
 */
export const Popover = RPopover.Root;
export const PopoverTrigger = RPopover.Trigger;
export const PopoverAnchor = RPopover.Anchor;
export const PopoverClose = RPopover.Close;

export type PopoverContentProps = React.ComponentPropsWithoutRef<
  typeof RPopover.Content
>;

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof RPopover.Content>,
  PopoverContentProps
>(
  (
    {
      className,
      align = "center",
      side = "bottom",
      sideOffset = 6,
      collisionPadding = 8,
      ...props
    },
    ref,
  ) => (
    <RPopover.Portal>
      <RPopover.Content
        ref={ref}
        align={align}
        side={side}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          "z-[70] w-60 rounded-xl border border-smoke bg-paper p-3",
          "shadow-[0_12px_32px_-8px_rgba(0,0,0,0.18),0_4px_12px_-4px_rgba(0,0,0,0.08)]",
          "outline-none",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          "origin-[var(--radix-popover-content-transform-origin)] duration-150",
          className,
        )}
        {...props}
      />
    </RPopover.Portal>
  ),
);
PopoverContent.displayName = "PopoverContent";
